/**
 * FotografiasFilters - Filtros multi-select horizontais para aba Fotografias
 *
 * MOCKUP GLOBAL: Layout horizontal com MultiSelectFilter
 * Filtros: Cliente, Tipo Geral, Classificação, Tipo Arquivo, Busca
 */
import { Search, Trash2 } from 'lucide-react';
import { MultiSelectFilter, FilterOption } from '../MultiSelectFilter';

interface FotografiasFiltersProps {
  // Opções de filtro (Cliente removido - agora usa filtro global no header)
  classificacaoOptions: FilterOption[];
  tipoGeralOptions: FilterOption[];
  tipoArquivoOptions: FilterOption[];

  // Valores selecionados (Cliente removido)
  selectedTipoGeral: (string | number)[];
  selectedClassificacaoIds: (string | number)[];
  selectedTipoArquivo: (string | number)[];
  searchTerm: string;

  // Setters (Cliente removido)
  setSelectedTipoGeral: (values: (string | number)[]) => void;
  setSelectedClassificacaoIds: (values: (string | number)[]) => void;
  setSelectedTipoArquivo: (values: (string | number)[]) => void;
  setSearchTerm: (value: string) => void;

  // Ações
  onClearAll: () => void;
  hasActiveFilters: boolean;
}

export function FotografiasFilters({
  classificacaoOptions,
  tipoGeralOptions,
  tipoArquivoOptions,
  selectedTipoGeral,
  selectedClassificacaoIds,
  selectedTipoArquivo,
  searchTerm,
  setSelectedTipoGeral,
  setSelectedClassificacaoIds,
  setSelectedTipoArquivo,
  setSearchTerm,
  onClearAll,
  hasActiveFilters,
}: FotografiasFiltersProps) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex flex-col lg:flex-row lg:items-end gap-4">
        {/* Grid de Filtros - Layout horizontal (Cliente removido - filtro global no header) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
          {/* Tipo Geral (Evidência / Relatório) */}
          <MultiSelectFilter
            label="Tipo Geral"
            options={tipoGeralOptions}
            selectedValues={selectedTipoGeral}
            onChange={setSelectedTipoGeral}
            placeholder="Todos"
            showTodos={true}
            showBadges={false}
          />

          {/* Classificação (nomes completos) */}
          <MultiSelectFilter
            label="Classificação"
            options={classificacaoOptions}
            selectedValues={selectedClassificacaoIds}
            onChange={setSelectedClassificacaoIds}
            placeholder="Todas"
            showTodos={true}
            showBadges={false}
          />

          {/* Tipo de Arquivo (Imagem / PDF) */}
          <MultiSelectFilter
            label="Tipo Arquivo"
            options={tipoArquivoOptions}
            selectedValues={selectedTipoArquivo}
            onChange={setSelectedTipoArquivo}
            placeholder="Todos"
            showTodos={true}
            showBadges={false}
          />

          {/* Campo de Busca */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Buscar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nome do arquivo..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors hover:border-blue-500 dark:hover:border-blue-400"
              />
            </div>
          </div>
        </div>

        {/* Botão Limpar Filtros */}
        <div className="flex items-center shrink-0 pb-0.5">
          <button
            onClick={onClearAll}
            disabled={!hasActiveFilters}
            className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors border border-transparent ${
              hasActiveFilters
                ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-100 dark:hover:border-red-800'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            title="Limpar todos os filtros"
          >
            <Trash2 className="w-4 h-4" />
            <span className="whitespace-nowrap hidden sm:inline">Limpar</span>
          </button>
        </div>
      </div>

      {/* Badges dos filtros ativos (resumo compacto) - Cliente removido, usa filtro global */}
      {hasActiveFilters && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-slate-500">Filtros ativos:</span>

            {selectedTipoGeral.length > 0 && (
              <FilterBadge
                label={selectedTipoGeral.map(v =>
                  tipoGeralOptions.find(o => o.value === v)?.label || v
                ).join(', ')}
                onRemove={() => setSelectedTipoGeral([])}
              />
            )}

            {selectedClassificacaoIds.length > 0 && (
              <FilterBadge
                label={`${selectedClassificacaoIds.length} classificaç${selectedClassificacaoIds.length > 1 ? 'ões' : 'ão'}`}
                onRemove={() => setSelectedClassificacaoIds([])}
              />
            )}

            {selectedTipoArquivo.length > 0 && (
              <FilterBadge
                label={selectedTipoArquivo.map(v =>
                  tipoArquivoOptions.find(o => o.value === v)?.label || v
                ).join(', ')}
                onRemove={() => setSelectedTipoArquivo([])}
              />
            )}

            {searchTerm.trim() && (
              <FilterBadge
                label={`"${searchTerm}"`}
                onRemove={() => setSearchTerm('')}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== FILTER BADGE ====================

interface FilterBadgeProps {
  label: string;
  onRemove: () => void;
}

function FilterBadge({ label, onRemove }: FilterBadgeProps) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-md">
      {label}
      <button
        onClick={onRemove}
        className="hover:bg-blue-100 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors"
        aria-label={`Remover filtro ${label}`}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </span>
  );
}

export default FotografiasFilters;
